import { Editor, Element as SlateElement, Node, Transforms } from 'slate';
import { CustomElement } from '../utils/element-renderer';
import { ContactCardElement } from './with-contact-cards';

/**
 * Type guards for our node types
 */
function isStopMessage(n: Node): n is CustomElement {
  return SlateElement.isElement(n) && n.type === 'stop-message';
}

function isImage(n: Node): n is CustomElement {
  return SlateElement.isElement(n) && n.type === 'image';
}

function isContactCard(n: Node): n is ContactCardElement {
  return SlateElement.isElement(n) && n.type === 'contact-card';
}

/**
 * Plugin to enforce document-wide structural invariants:
 * 1. Must have exactly one stop message
 * 2. If image or contact card exists, order must be: [content] -> [stop message] -> [attachment]
 * 3. If no attachment, order must be: [content] -> [stop message]
 * 4. Only one MMS attachment (image or contact card) can exist at a time
 *
 * Uses node-level normalization rules for a more declarative approach.
 */
export const withDocumentStructure = (editor: Editor) => {
  const { normalizeNode } = editor;

  editor.normalizeNode = ([node, path]) => {
    // Handle editor-level normalization first
    if (Editor.isEditor(node)) {
      // Get all special nodes
      const stopMessages = Array.from(
        Editor.nodes(editor, {
          at: [],
          match: isStopMessage
        })
      );

      const images = Array.from(
        Editor.nodes(editor, {
          at: [],
          match: isImage
        })
      );

      const contactCards = Array.from(
        Editor.nodes(editor, {
          at: [],
          match: isContactCard
        })
      );

      // 1. Ensure exactly one stop message exists
      if (stopMessages.length === 0) {
        Transforms.insertNodes(
          editor,
          {
            type: 'stop-message',
            messageText: 'Text STOP to unsubscribe',
            children: [{ text: '' }]
          } as CustomElement,
          { at: [node.children.length] }
        );
        return;
      }

      if (stopMessages.length > 1) {
        // Keep only the last stop message
        const [_, ...remove] = stopMessages.reverse();
        remove.forEach(([, removePath]) => {
          Transforms.removeNodes(editor, { at: removePath });
        });
        return;
      }

      // 2. Handle MMS attachment exclusivity
      const attachments = [...images, ...contactCards];
      if (attachments.length > 1) {
        // Keep only the last attachment
        const [_, ...remove] = attachments.reverse();
        remove.forEach(([, removePath]) => {
          Transforms.removeNodes(editor, { at: removePath });
        });
        return;
      }

      // Let Slate handle other editor-level normalizations
      normalizeNode([node, path]);
    }

    // Handle element-level normalization
    if (SlateElement.isElement(node)) {
      const attachments = [
        ...Array.from(Editor.nodes(editor, { at: [], match: isImage })),
        ...Array.from(Editor.nodes(editor, { at: [], match: isContactCard }))
      ];

      // Normalize attachment nodes (image or contact card)
      if (node.type === 'image' || node.type === 'contact-card') {
        // Attachment must be the last element
        if (path[0] < editor.children.length - 1) {
          Transforms.moveNodes(editor, {
            at: path,
            to: [editor.children.length - 1]
          });
          return;
        }
      }

      // Normalize stop message nodes
      if (node.type === 'stop-message') {
        const targetIndex =
          attachments.length > 0
            ? editor.children.length - 2 // Before attachment
            : editor.children.length - 1; // At the end

        if (path[0] !== targetIndex) {
          Transforms.moveNodes(editor, {
            at: path,
            to: [targetIndex]
          });
          return;
        }
      }
    }

    // Let Slate handle other normalizations
    normalizeNode([node, path]);
  };

  return editor;
};

// Helper function to check if editor already has an MMS attachment
export function hasAttachment(editor: Editor): boolean {
  return hasImage(editor) || hasContactCard(editor);
}

// Helper function to check if editor already has an image
export function hasImage(editor: Editor): boolean {
  const [match] = Editor.nodes(editor, {
    at: [],
    match: isImage
  });
  return !!match;
}

// Helper function to check if editor already has a contact card
export function hasContactCard(editor: Editor): boolean {
  const [match] = Editor.nodes(editor, {
    at: [],
    match: isContactCard
  });
  return !!match;
}
